import {
  type BufferAttribute,
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  type InterleavedBufferAttribute,
  type Material,
  Matrix4,
  Mesh,
  type Object3D
} from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

type RackLayout = {
  cargoName: string;
  prefix: string;
  structureName: string;
};

type GeometryPart = {
  geometry: BufferGeometry;
  material: Material;
};

type PrimitiveTemplate = {
  geometry: BufferGeometry;
  materialIndex: number;
};

type AttributeSchema = {
  itemSize: number;
};

const rackLayouts: RackLayout[] = [
  {
    cargoName: "pallet-rack-cargo",
    prefix: "dxf-pallet-rack-",
    structureName: "pallet-rack-structure"
  },
  {
    cargoName: "bin-rack-cargo",
    prefix: "dxf-bin-rack-",
    structureName: "bin-rack-structure"
  }
];

export function mergeRackExportGeometry(root: Group) {
  const racksRoot = root.getObjectByName("dxf-racks");
  if (!(racksRoot instanceof Group)) {
    return [];
  }

  root.updateWorldMatrix(true, true);
  const ownedGeometries: BufferGeometry[] = [];
  try {
    for (const rack of [...racksRoot.children]) {
      const layout = rackLayouts.find(({ prefix }) => rack.name.startsWith(prefix));
      if (!layout || !(rack instanceof Group)) {
        continue;
      }
      mergeRackSection(rack, layout.structureName, ownedGeometries);
      mergeRackSection(rack, layout.cargoName, ownedGeometries);
    }
    return ownedGeometries;
  } catch (error) {
    ownedGeometries.forEach((geometry) => {
      geometry.dispose();
    });
    throw error;
  }
}

function mergeRackSection(rack: Group, sectionName: string, ownedGeometries: BufferGeometry[]) {
  const section = rack.children.find((child) => child.name === sectionName);
  if (!section) {
    return;
  }

  const merged = createMergedSectionMesh(rack, section, sectionName);
  rack.remove(section);
  if (merged) {
    rack.add(merged);
    ownedGeometries.push(merged.geometry);
  }
}

function createMergedSectionMesh(rack: Group, section: Object3D, name: string) {
  const rackWorldInverse = rack.matrixWorld.clone().invert();
  const parts: GeometryPart[] = [];

  try {
    section.traverse((object) => {
      if (object instanceof InstancedMesh) {
        appendInstancedMeshParts(object, rackWorldInverse, parts);
      } else if (object instanceof Mesh) {
        appendMeshParts(object, rackWorldInverse.clone().multiply(object.matrixWorld), parts);
      }
    });

    if (parts.length === 0) {
      return;
    }

    normalizeAttributes(parts);
    const geometry = mergeGeometries(
      parts.map((part) => part.geometry),
      false
    );
    if (!geometry) {
      throw new Error(`无法合并货架导出几何：${name}`);
    }

    geometry.name = name;
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    const materials = applyMaterialGroups(geometry, parts);
    const mesh = new Mesh(geometry, materials.length === 1 ? materials[0] : materials);
    mesh.name = name;
    return mesh;
  } finally {
    parts.forEach((part) => {
      part.geometry.dispose();
    });
  }
}

function appendInstancedMeshParts(
  mesh: InstancedMesh,
  rackWorldInverse: Matrix4,
  parts: GeometryPart[]
) {
  const instanceMatrix = new Matrix4();
  const baseMatrix = rackWorldInverse.clone().multiply(mesh.matrixWorld);
  const templates = createPrimitiveTemplates(mesh.geometry, Array.isArray(mesh.material));
  try {
    for (let index = 0; index < mesh.count; index += 1) {
      mesh.getMatrixAt(index, instanceMatrix);
      appendPrimitiveParts(
        templates,
        mesh.material,
        baseMatrix.clone().multiply(instanceMatrix),
        parts
      );
    }
  } finally {
    templates.forEach((template) => {
      template.geometry.dispose();
    });
  }
}

function appendMeshParts(mesh: Mesh, matrix: Matrix4, parts: GeometryPart[]) {
  const templates = createPrimitiveTemplates(mesh.geometry, Array.isArray(mesh.material));
  try {
    appendPrimitiveParts(templates, mesh.material, matrix, parts);
  } finally {
    templates.forEach((template) => {
      template.geometry.dispose();
    });
  }
}

function appendPrimitiveParts(
  templates: PrimitiveTemplate[],
  material: Material | Material[],
  matrix: Matrix4,
  parts: GeometryPart[]
) {
  const materials = Array.isArray(material) ? material : [material];
  for (const template of templates) {
    const primitiveMaterial = materials[template.materialIndex];
    if (!primitiveMaterial) {
      throw new Error(`货架模型缺少材质索引：${template.materialIndex}`);
    }

    const geometry = template.geometry.clone().applyMatrix4(matrix);
    if (matrix.determinant() < 0) {
      reverseTriangleWinding(geometry);
    }
    parts.push({ geometry, material: primitiveMaterial });
  }
}

function createPrimitiveTemplates(geometry: BufferGeometry, useMaterialGroups: boolean) {
  if (Object.keys(geometry.morphAttributes).length > 0) {
    throw new Error("暂不支持合并带有变形目标的货架模型");
  }

  const source = geometry.clone();
  try {
    if (!source.getAttribute("normal")) {
      source.computeVertexNormals();
    }
    const position = source.getAttribute("position");
    if (!position) {
      throw new Error("货架模型缺少 POSITION 顶点属性");
    }

    const elementCount = source.index?.count ?? position.count;
    return createPrimitiveRanges(source, elementCount, useMaterialGroups).map(
      ({ count, materialIndex, start }) => {
        return {
          geometry: createPrimitiveGeometry(source, start, count),
          materialIndex
        };
      }
    );
  } finally {
    source.dispose();
  }
}

function createPrimitiveRanges(
  geometry: BufferGeometry,
  elementCount: number,
  useMaterialGroups: boolean
) {
  const drawStart = Math.max(0, geometry.drawRange.start);
  const drawEnd = Math.min(
    elementCount,
    Number.isFinite(geometry.drawRange.count) ? drawStart + geometry.drawRange.count : elementCount
  );
  const groups =
    useMaterialGroups && geometry.groups.length > 0
      ? geometry.groups
      : [{ count: elementCount, materialIndex: 0, start: 0 }];

  return groups.flatMap((group) => {
    const start = Math.max(group.start, drawStart);
    const end = Math.min(group.start + group.count, drawEnd);
    const count = Math.max(0, end - start);
    if (count === 0) {
      return [];
    }
    if (start % 3 !== 0 || count % 3 !== 0) {
      throw new Error("货架模型的绘制范围不是完整三角面");
    }
    return [{ count, materialIndex: group.materialIndex ?? 0, start }];
  });
}

function createPrimitiveGeometry(source: BufferGeometry, start: number, count: number) {
  const sourceVertexIndices: number[] = [];
  const remappedVertexIndices = new Map<number, number>();
  const indices: number[] = [];

  for (let offset = 0; offset < count; offset += 1) {
    const sourceVertexIndex = source.index?.getX(start + offset) ?? start + offset;
    let targetVertexIndex = remappedVertexIndices.get(sourceVertexIndex);
    if (targetVertexIndex === undefined) {
      targetVertexIndex = sourceVertexIndices.length;
      sourceVertexIndices.push(sourceVertexIndex);
      remappedVertexIndices.set(sourceVertexIndex, targetVertexIndex);
    }
    indices.push(targetVertexIndex);
  }

  const primitive = new BufferGeometry();
  for (const [name, attribute] of Object.entries(source.attributes)) {
    primitive.setAttribute(name, selectAttributeVertices(attribute, sourceVertexIndices));
  }
  primitive.setIndex(indices);
  return primitive;
}

function selectAttributeVertices(
  attribute: BufferAttribute | InterleavedBufferAttribute,
  sourceVertexIndices: number[]
) {
  const values = new Float32Array(sourceVertexIndices.length * attribute.itemSize);
  for (
    let targetVertexIndex = 0;
    targetVertexIndex < sourceVertexIndices.length;
    targetVertexIndex += 1
  ) {
    for (let component = 0; component < attribute.itemSize; component += 1) {
      values[targetVertexIndex * attribute.itemSize + component] = attribute.getComponent(
        sourceVertexIndices[targetVertexIndex],
        component
      );
    }
  }
  return new Float32BufferAttribute(values, attribute.itemSize);
}

function normalizeAttributes(parts: GeometryPart[]) {
  const schema = new Map<string, AttributeSchema>();
  for (const { geometry } of parts) {
    for (const [name, attribute] of Object.entries(geometry.attributes)) {
      const existing = schema.get(name);
      if (existing && existing.itemSize !== attribute.itemSize) {
        throw new Error(`货架模型的 ${name} 顶点属性格式不一致`);
      }
      schema.set(name, { itemSize: attribute.itemSize });
    }
  }

  for (const { geometry } of parts) {
    const vertexCount = geometry.getAttribute("position").count;
    for (const [name, { itemSize }] of schema) {
      if (!geometry.getAttribute(name)) {
        geometry.setAttribute(name, createDefaultAttribute(name, vertexCount, itemSize));
      }
    }
  }
}

function createDefaultAttribute(name: string, vertexCount: number, itemSize: number) {
  const values = new Float32Array(vertexCount * itemSize);
  if (name === "color") {
    values.fill(1);
  } else if (name === "normal" && itemSize >= 2) {
    for (let index = 0; index < vertexCount; index += 1) {
      values[index * itemSize + 1] = 1;
    }
  } else if (name === "tangent") {
    for (let index = 0; index < vertexCount; index += 1) {
      values[index * itemSize] = 1;
      if (itemSize >= 4) {
        values[index * itemSize + 3] = 1;
      }
    }
  }
  return new Float32BufferAttribute(values, itemSize);
}

function reverseTriangleWinding(geometry: BufferGeometry) {
  const indexAttribute = geometry.index;
  if (!indexAttribute) {
    throw new Error("货架合并几何缺少三角面索引");
  }
  for (let start = 0; start < indexAttribute.count; start += 3) {
    const second = indexAttribute.getX(start + 1);
    indexAttribute.setX(start + 1, indexAttribute.getX(start + 2));
    indexAttribute.setX(start + 2, second);
  }

  const tangent = geometry.getAttribute("tangent");
  if (tangent?.itemSize === 4) {
    for (let index = 0; index < tangent.count; index += 1) {
      tangent.setW(index, -tangent.getW(index));
    }
  }
}

function applyMaterialGroups(geometry: BufferGeometry, parts: GeometryPart[]) {
  const materials: Material[] = [];
  const materialIndices = new Map<Material, number>();
  let elementOffset = 0;

  for (const part of parts) {
    let materialIndex = materialIndices.get(part.material);
    if (materialIndex === undefined) {
      materialIndex = materials.length;
      materials.push(part.material);
      materialIndices.set(part.material, materialIndex);
    }
    const elementCount = part.geometry.index?.count ?? 0;
    appendMaterialGroup(geometry, elementOffset, elementCount, materialIndex);
    elementOffset += elementCount;
  }

  if (materials.length === 1) {
    geometry.clearGroups();
  }
  return materials;
}

function appendMaterialGroup(
  geometry: BufferGeometry,
  start: number,
  count: number,
  materialIndex: number
) {
  const current = geometry.groups.at(-1);
  if (current?.materialIndex === materialIndex && current.start + current.count === start) {
    current.count += count;
  } else {
    geometry.addGroup(start, count, materialIndex);
  }
}
